import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button } from '@heroui/react';
import {
  PracticeCard,
  PracticeCardSkeleton,
  practicePaletteFor,
  shuffleByStatusTier,
  usePracticeScenarios,
  type PracticeKind,
  type PracticeScenario,
} from '@/entities/practice';
import { ROUTES } from '@/shared/config';
import { haptics } from '@/shared/lib/telegram/haptics';
import { ErrorBlock } from '@/shared/ui';
import { useInDesktopShell } from '@/shared/lib/desktopLayout';
import { cn } from '@/shared/lib/cn';

/** Сколько карточек показываем на главном — сетка 2×3, полный список открывается по кнопке «Показать ещё» (`pages/topics`). */
const PREVIEW_LIMIT = 6;

const KIND_FILTERS: { key: PracticeKind; labelKey: string }[] = [
  { key: 'roleplay', labelKey: 'filterRoleplay' },
  { key: 'idiom', labelKey: 'filterIdiom' },
];

/**
 * Секция «Готовые темы» на главном экране: ролевые игры и идиомы вперемешку
 * одной сеткой (раньше были два разных виджета — `RoleplayCarousel`/
 * `IdiomCarousel`, каждый своей горизонтальной лентой). Сверху —
 * переключатель вида (Ролевые игры/Идиомы, без «Все» — ровно два пункта):
 * он фильтрует именно эту превью-сетку, а не уводит на другой экран. Кнопка
 * «Показать ещё» ведёт на `RoleplayAllPage`/`IdiomAllPage` (смотря какой вид
 * сейчас выбран) — там уже полный список одного вида со статус-фильтром
 * (Все/Новое/Не пройденное/Пройденное, тут его нет, это только превью).
 */
export function TopicsSection() {
  // На компьютере колонка 640 px: в две колонки квадратные карточки выходили
  // по 290 px, и на экран влезал один ряд. Три — шесть карточек превью в два ряда.
  const inDesktopShell = useInDesktopShell();
  const { t } = useTranslation('TopicsSection');
  const navigate = useNavigate();
  const [kindFilter, setKindFilter] = useState<PracticeKind>('roleplay');

  // Из кеша между заходами на главную: статус «просмотрено» кеш получает
  // сразу при открытии карточки, «пройдено» — сбросом на выходе из чата (см.
  // `usePracticeScenarios`). Сбой вида — карточка ошибки на месте его сетки
  // (виды грузятся отдельно, второй при этом живёт).
  const roleplays = usePracticeScenarios('roleplay');
  const idioms = usePracticeScenarios('idiom');
  const isReady = !roleplays.isLoading && !idioms.isLoading;
  const scenarios = useMemo<PracticeScenario[] | null>(
    () => (isReady ? [...(roleplays.data ?? []), ...(idioms.data ?? [])] : null),
    [isReady, roleplays.data, idioms.data],
  );

  // Перемешано внутри уровня (новое/непройденное/пройденное) и обрезано до
  // PREVIEW_LIMIT — пересчитывается только при смене данных или фильтра
  // (`useMemo`), а не при каждом рендере, иначе сетка переставляла бы карточки
  // сама собой.
  const preview = useMemo(() => {
    if (scenarios === null) return null;
    return shuffleByStatusTier(scenarios.filter((s) => s.kind === kindFilter)).slice(
      0,
      PREVIEW_LIMIT,
    );
  }, [scenarios, kindFilter]);

  const openAll = () => {
    haptics.advance();
    void navigate(kindFilter === 'roleplay' ? ROUTES.roleplayAll : ROUTES.idiomAll);
  };

  // Отметка «просмотрено» и сама карточка целиком — на отдельном
  // экране-превью (см. `pages/roleplay`/`pages/idiom`), не здесь: тап по
  // карточке сначала показывает содержимое, а не сразу что-то запускает.
  const openScenario = (scenario: PracticeScenario) =>
    void navigate(
      `${scenario.kind === 'roleplay' ? ROUTES.roleplay : ROUTES.idiom}/${scenario.id}`,
    );

  const selected = kindFilter === 'roleplay' ? roleplays : idioms;
  const selectedFailed = selected.error != null;

  if (
    !selectedFailed &&
    roleplays.error == null &&
    idioms.error == null &&
    scenarios !== null &&
    scenarios.length === 0
  ) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3">
      <h2 className="text-lg font-bold text-ink">{t('heading')}</h2>

      <div className="flex items-center gap-3">
        {KIND_FILTERS.map(({ key, labelKey }, index) => (
          <div key={key} className="flex items-center gap-3">
            {index > 0 && (
              <span className="text-border" aria-hidden="true">
                |
              </span>
            )}
            <button
              type="button"
              onClick={() => setKindFilter(key)}
              className={
                kindFilter === key
                  ? 'text-[15px] font-bold text-ink'
                  : 'text-[15px] text-ink-subtle active:opacity-70'
              }
            >
              {t(labelKey)}
            </button>
          </div>
        ))}
      </div>

      {selectedFailed && <ErrorBlock onRetry={selected.reload} />}

      {!selectedFailed && (
        <div className={cn('grid gap-4', inDesktopShell ? 'grid-cols-3' : 'grid-cols-2')}>
          {preview === null
            ? Array.from({ length: PREVIEW_LIMIT }, (_, index) => (
                <PracticeCardSkeleton key={index} />
              ))
            : preview.map((scenario, index) => (
                <PracticeCard
                  key={scenario.id}
                  scenario={scenario}
                  palette={practicePaletteFor(index)}
                  onOpen={() => openScenario(scenario)}
                />
              ))}
        </div>
      )}

      {!selectedFailed && preview !== null && (
        <Button
          variant="outline"
          fullWidth
          size="lg"
          onPress={openAll}
          className="shadow-[0_6px_16px_rgba(0,0,0,0.12)]"
        >
          {t('showMore')}
        </Button>
      )}
    </div>
  );
}
